// Structure-of-arrays backing for the effect pools — the shape the storm
// clouds already use, pulled out so tracers, bombs, trails, contrails, and
// the particle system can all sit on it.
//
// The pools it replaces each held an array of little objects: a particle was
// `{pos: Vector3, vel: Vector3, life, size, ...}`, and a broadside spawns a
// hundred and seventy of them at once — three heap objects apiece, every
// time a battleship speaks. Worse, the pools were bounded with `shift()`,
// which walks the whole array to drop one particle, and compacted the dead
// each frame by building a fresh `keep` array. None of that is arithmetic the
// effect actually needs; it is bookkeeping the garbage collector pays for
// later, mid-fight, as a dropped frame.
//
// Here a pool is a fixed set of parallel typed arrays and a live count.
// Nothing is allocated after construction. Slots stay in age order — oldest
// first — because the pools depend on it: a trail must lose its tail, not a
// random segment out of its middle, and the translucent ones are blended in
// submission order.

/**
 * @param {number} capacity  hard cap; the pool never grows past it
 * @param {{f32?: string[], u32?: string[], u8?: string[]}} spec  field names
 *   by storage class. Fields are reachable as `pool.f.<name>` (float32),
 *   `pool.u.<name>` (uint32), `pool.b.<name>` (uint8).
 *
 * PER-ELEMENT ACCESS: lift the arrays out before a hot loop —
 * `const { px, py, pz } = pool.f;` — and index the locals. Writing
 * `pool.f.px[i]` inside the loop pays a property load per element per field,
 * which on a 700-particle pool with a dozen fields is most of the frame.
 */
export class SoAPool {
  constructor(capacity, spec) {
    this.capacity = capacity;
    this.count = 0;
    // Plain objects, not null-prototype ones: every pool built from the same
    // spec ends up with the same hidden class, so `pool.f.px` stays an
    // inline-cached property load. A null-prototype object with keys added in
    // a loop drops into dictionary mode, where the same lookup is a hash
    // probe — measured at ~12x slower when done per element in a hot loop.
    // Hot loops should still lift the arrays into locals once (see below);
    // this just makes the occasional stray access cheap too.
    this.f = {};
    this.u = {};
    this.b = {};
    // Backing arrays grouped by element type. Kept apart so the slot copy
    // below sees one kind of typed array per loop: a single mixed list makes
    // `all[i][to] = all[i][from]` megamorphic, and the compaction pass runs
    // over every live particle every frame.
    this._f32 = [];
    this._u32 = [];
    this._u8 = [];
    for (const name of spec.f32 ?? []) {
      this.f[name] = new Float32Array(capacity);
      this._f32.push(this.f[name]);
    }
    for (const name of spec.u32 ?? []) {
      this.u[name] = new Uint32Array(capacity);
      this._u32.push(this.u[name]);
    }
    for (const name of spec.u8 ?? []) {
      this.b[name] = new Uint8Array(capacity);
      this._u8.push(this.b[name]);
    }
    this._all = [...this._f32, ...this._u32, ...this._u8];
  }

  /** Copy every field of slot `from` over slot `to`. */
  copySlot(from, to) {
    const f = this._f32;
    for (let i = 0; i < f.length; i++) f[i][to] = f[i][from];
    const u = this._u32;
    for (let i = 0; i < u.length; i++) u[i][to] = u[i][from];
    const b = this._u8;
    for (let i = 0; i < b.length; i++) b[i][to] = b[i][from];
  }

  /**
   * Drop the `k` oldest slots, sliding the survivors down. One native memmove
   * per field instead of k array shifts — which matters because the callers
   * evict in bursts (a muzzle blast asks for room for ~170 at once).
   */
  dropOldest(k) {
    if (k <= 0) return;
    if (k >= this.count) {
      this.count = 0;
      return;
    }
    const all = this._all;
    for (let i = 0; i < all.length; i++) all[i].copyWithin(0, k, this.count);
    this.count -= k;
  }

  /**
   * Make room for `need` incoming items and return the index to write the
   * first one at. Evicts the oldest when the pool is full, exactly as the
   * old per-item `shift()`-before-`push()` did, but in one move.
   *
   * Returns `{at, skip}`: write items `skip..need` starting at index `at`.
   * `skip` is only ever non-zero if a single spawn asks for more than the
   * whole pool, in which case the last `capacity` of the batch survive.
   */
  makeRoom(need) {
    if (need >= this.capacity) {
      // One spawn bigger than the whole pool: everything already here is
      // gone, and only the tail of the batch fits. The big explosions do hit
      // this against a small pool.
      this.count = this.capacity;
      return { at: 0, skip: need - this.capacity };
    }
    const over = this.count + need - this.capacity;
    if (over > 0) this.dropOldest(over);
    const at = this.count;
    this.count += need;
    return { at, skip: 0 };
  }

  /** Append one slot and return its index, evicting the oldest if full. */
  push() {
    if (this.count >= this.capacity) this.dropOldest(1);
    return this.count++;
  }

  /** Forget everything, without touching the backing arrays. */
  clear() {
    this.count = 0;
  }
}
